/**
 * @module mapbs.components.infra3d.infra3dComponent
 */
import angular from 'angular';
import {equals} from 'ol/coordinate.js';
import olFeature from 'ol/Feature.js';
import {unByKey} from 'ol/Observable.js';
import olGeomPoint from 'ol/geom/Point.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleText from 'ol/style/Text.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olSourceVector from 'ol/source/Vector.js';
import olEventsEventType from 'ol/events/EventType.js';
import {listen} from 'ol/events.js';
import olLayerVector from 'ol/layer/Vector.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import ngeoToolActivate from 'ngeo/misc/ToolActivate.js';
import {infra3d} from './infra3dapi.js';
import './infra3dComponent.scss';

import ngeoFeatureOverlayMgr from 'ngeo/map/FeatureOverlayMgr';

const moduleInfra3d = angular.module('mapbsInfra3dModule', []);

moduleInfra3d.run(
    /* ngInject */ ($templateCache) => {
        $templateCache.put('mapbs/infra3d', require('./infra3dComponent.html'));
    }
);

/** @type {!angular.Component} */
const Infra3dComponent = {
    controller: 'mapbsInfra3dController as ctrl',
    templateUrl: 'mapbs/infra3d',
    bindings: {
        'active': '=',
        'map': '<',
        'followCenter': '<?',
        'infra3dClient': '<',
    },
};

moduleInfra3d.component('mapbsInfra3d', Infra3dComponent);

class Infra3dController {
    /**
   * @param {!angular.Scope} $scope Scope.
   *     manager.
   * @param {!ngeoToolActivateMgr} ngeoToolActivateMgr Ngeo ToolActivate manager
   *     service.
   * @private
   * @ngInject
   * @ngdoc controller
   * @ngname NgeoInfra3dController
   */
    constructor($scope, ngeoToolActivateMgr) {
    // Binding properties

        /**
     * @type {boolean}
     * @export
     */
        this.active;

        $scope.$watch(() => this.active, this.handleActiveChange_.bind(this));

        /**
     * @type {!ol.Map}
     * @export
     */
        this.map;

        /**
     * @type {boolean}
     * @export
     */
        this.followCenter = false;

        /**
     * URL of Infra3D client.
     * @type {string}
     * @export
     * @see https://www.infra3d.ch/api/apidoc/Reference/#init
     */
        this.infra3dClient;

        /**
     * Username used to login into Infra3D client.
     * @type {?string}
     * @export
     */
        this.username;

        /**
     * Password used to login into Infra3D client.
     * @type {?string}
     * @export
     */
        this.password;

        // Injected properties

        /**
     * @type {!angular.Scope}
     * @private
     */
        this.scope_ = $scope;

        /**
     * @type {!ngeo.ToolActivateMgr}
     * @private
     */
        this.ngeoToolActivateMgr_ = ngeoToolActivateMgr;

        // Inner properties

        /**
     * State on whether  plugin has been initialized already.
     * @type {boolean}
     * @private
     */
        this.infra3dInitialized_ = false;

        /**
     * The current location in the OpenLayers' map view projection.
     * @type {olCoordinate|null}
     * @private
     */
        this.location_ = null;

        /**
     * @private
     */
        this.featureOverlay_ = ngeoFeatureOverlayMgr.getFeatureOverlay();

        /**
     * Feature representing the current position.
     * @type {!olFeature}
     * @private
     */
        this.feature_ = new olFeature();

        /**
     * Geometry of the current position.
     * @type {olGeomPoint}
     * @private
     */
        this.point_ = new olGeomPoint([0, 0]);

        this.feature_.setGeometry(this.point_);

        /**
     * @type {olStyleText}
     * @private
     */
        this.textStyle_ = new olStyleText({
            fill: new olStyleFill({color: '#ff0000'}),
            font: 'normal 30px FontAwesome',
            rotation: -Math.PI / 4, // rotation in radians
            stroke: new olStyleStroke({color: '#ffffff', width: 3}),
            text: '\uf124',
        });

        /**
     * Style for the feature.
     * @type {olStyleStyle}
     * @private
     */
        this.featureStyle_ = new olStyleStyle({
            text: this.textStyle_,
        });

        this.feature_.setStyle(this.featureStyle_);

        /**
     * @type {olEventsKey|null}
     * @private
     */
        this.handleMapClickListenerKey_ = null;

        /**
     * Available media.
     * @type {olSourceVector}
     * @private
     */
        this.ennSource_ = new olSourceVector();

        /**
     * @type {olLayerVector}
     * @private
     */
        this.ennLayer_ = new olLayerVector({
            source: this.ennSource_,
            style: new olStyleStyle({
                stroke: new olStyleStroke({
                    color: '#0300cc',
                    width: 3.0,
                }),
            }),
        });
    }

    /**
   * Called on initialization of the controller.
   */
    $onInit() {
        const infra3DToolActivate = new ngeoToolActivate(this, 'active');
        this.ngeoToolActivateMgr_.registerTool('mapTools', infra3DToolActivate);

        // === Watchers ===

        // (1) Watch for any change in the location
        this.scope_.$watch(
            () => this.location_,
            this.handleLocationChange_.bind(this)
        );

        // (2) Watch for both the active and location. When we have both, the
        //     state is considered 'ready'.
        this.scope_.$watch(() => {
            const isActive = this.active;
            const hasLocation = this.location_ !== null;
            return isActive && hasLocation;
        }, this.handleReadyChange_.bind(this));
    }

    /**
   * @private
   */
    initComponent_() {
    // Initialize location with current center of map.
        this.location_ = this.map.getView().getCenter() || [0, 0];

        // Infra3D iframe options
        const divId = 'infra3d';
        const infra3dOptions = {
            'easting': this.location_[0],
            'northing': this.location_[1],
            'epsg': 2056,
            'map': false,
            'layer': false,
            'navigation': true,
        };


        // Init Infra3D iframe
        infra3d.init(
            divId,
            this.infra3dClient,
            infra3dOptions,
            () => {

                // Register listener when position changes wihtin infra3d
                infra3d.setOnPositionChanged(this.handleInfra3DOnPositionChanged, this);

                // // Set infra3d state to initialized
                // this.infra3dInitialized_ = true;

                // Get available media from infra3d as layer
                infra3d.getEnn(
                    2056,
                    function (enn) {
                        const geojson = new olFormatGeoJSON();
                        const features = geojson.readFeatures(enn);
                        this.ennSource_.addFeatures(features);
                        this.map.removeLayer(this.ennLayer_);
                        this.map.addLayer(this.ennLayer_);
                    },
                    this,
                    null
                );
            },
            this
        );
    }

    /**
   * Called when the 'active' property of this component changes.
   * @param {boolean} active Active.
   * @private
   */
    handleActiveChange_(active) {
        if (active) {
            if (!this.infra3dInitialized_) {
                this.initComponent_();
                this.infra3dInitialized_ = true;
            }
            this.handleMapClickListenerKey_ = listen(
                this.map,
                olEventsEventType.CLICK,
                this.handleMapClick_,
                this
            );
        } else {
            unByKey(this.handleMapClickListenerKey_);
            this.handleMapClickListenerKey_ = null;
        }
    }

    getInfra3dOptions_() {
    // Infra3D options
    // see: https://www.infra3d.ch/api/apidoc/Reference/#init
        const options = {
            'easting': this.location_[0],
            'northing': this.location_[1],
            'epsg': 2056,
            'map': false,
            'layer': false,
            'navigation': true,
        };

        return options;
    }

    openPopup() {
    // Use the same options as in initComponent_
        const options = this.getInfra3dOptions_();
        // override defaults
        options['map'] = true; // show map
        const queryString = Object.keys(options)
      .map((key) => `${key}=${options[key]}`)
      .join('&');
        const url = `${this.infra3dClient}?${queryString}`;

        // Open popup
        const popup = window.open(url, 'infra3dPopup');
        if (window.focus) {
            popup.focus();
        }
    }

    /**
   * Called when the 'location' property of this component changes.
   * @param {?olCoordinate} location Location, in OL map view projection.
   * @param {?olCoordinate} oldLocation The previous location.
   * @private
   */
    handleLocationChange_(location, oldLocation) {
    // Don't update if new location is empty
        if (location === null) {
            return;
        }

        // No need to do anything if the old value equals the new value
        if (oldLocation !== null && equals(location, oldLocation)) {
            return;
        }

        // Update point coordinates
        this.point_.setCoordinates(location);
    }

    /**
   * Called when the map is clicked while this component is active. Update the
   * location accordingly.
   * @param {olMapBrowserEvent} evt The map browser event being fired.
   * @private
   */
    handleMapClick_(evt) {
        if (this.infra3dInitialized_) {
            this.location_ = evt.coordinate;
            this.scope_.$apply();

            // send location to infra3d
            infra3d.loadAtPosition(this.location_[0], this.location_[1], {
                'epsg': 2056,
            });
        }
    }

    /**
   * Called when the component 'virtual ready' state changes.
   *
   * When ready:
   *  - add the feature to the overlay
   *
   * When not ready:
   *  - remove the feature from the overlay
   *
   * @param {boolean} ready Whether the component is ready or not.
   * @param {boolean} oldReady Previous ready value.
   * @private
   */
    handleReadyChange_(ready, oldReady) {
        if (ready === oldReady) {
            return;
        }

        if (ready) {
            // Add current position to feature overlay
            this.featureOverlay_.addFeature(this.feature_);

            // Display available media
            this.ennLayer_.setVisible(true);
        } else {
            // Remove current position from feature overlay
            this.featureOverlay_.removeFeature(this.feature_);

            // Hide available media
            this.ennLayer_.setVisible(false);
        }
    }

    /**
   * Called when the user logs out of the infra3d plugin.
   * @private
   */
    handleLogout_() {
    // Register listener when position changes within infra3d
        infra3d.unsetOnPositionChanged();

        // Clear available media
        this.ennSource_.clear();
    }

    /**
   * Event called by the Infra3D-API when a position gets updated.
   *
   * Note: There are eleven parameters, but we only use the first two.
   * @param {number} easting Coordinate of the current position.
   * @param {number} northing Coordinate of the current position.
   * @param {number} height Coordinate of the current position.
   * @param {number} epsg Epsg-code for the coordinates of the current position.
   * @param {number} orientation Horizontal orientation of the camera (Azimuth).
   * @see https://www.infra3d.ch/api/apidoc/Reference/#onpositionchanged
   */
    handleInfra3DOnPositionChanged(easting, northing, height, epsg, orientation) {
    // Set icon location
        this.location_ = /** @type{olCoordinate} */ ([easting, northing]);

        // Set icon orientation
        let rotation = orientation - 45; // icon image offset
        rotation = ((rotation % 360) + 360) % 360; // modulo 360 to enforce positive result
        rotation *= Math.PI / 180; // convert to radians
        this.textStyle_.setRotation(rotation);

        // Move map center to new location if followCenter is enabled
        if (this.followCenter) {
            this.map.getView().setCenter(this.location_);
        }

        this.scope_.$apply();
    }
}

moduleInfra3d.controller('mapbsInfra3dController', Infra3dController);

export default moduleInfra3d;
